import { NgComponentOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    effect,
    inject,
    input,
} from '@angular/core';
import { QpxMetadata } from '../../metadata';
import type {
    FilePreviewInfoPanelConfig,
    FilePreviewPanelSide,
} from '../models';

export function resolvePreviewPanelDomId(
    side: FilePreviewPanelSide,
    panelId: string
): string {
    return `file-preview-${side}-panel-${panelId}`;
}

@Component({
    selector: 'qpx-preview-panel',
    imports: [NgComponentOutlet, QpxMetadata],
    templateUrl: './preview-panel.html',
    styleUrl: './preview-panel.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        role: 'complementary',
        '[hidden]': '!visible()',
        '[id]': 'panelId()',
        '[class.left]': "side() === 'left'",
        '[class.right]': "side() === 'right'",
        '[class.content-scroll]': 'panel()?.scrollable === false',
        '[attr.aria-label]': "panel()?.ariaLabel ?? 'Document information'",
    },
})
export class PreviewPanel {
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

    //#region Inputs
    public readonly visible = input(true);
    public readonly side = input.required<FilePreviewPanelSide>();
    public readonly panelId = input.required<string>();
    public readonly fileName = input.required<string>();
    public readonly panel = input<FilePreviewInfoPanelConfig | undefined>(
        undefined
    );
    //#endregion

    constructor() {
        effect(() => {
            if (this.visible()) return;
            const panel = this.elementRef.nativeElement;
            const document = panel.ownerDocument;
            if (!panel.contains(document.activeElement)) return;
            queueMicrotask(() => {
                if (this.visible() || !panel.contains(document.activeElement))
                    return;
                const preview =
                    panel.closest('qpx-file-preview') ?? panel.parentElement;
                const buttons = Array.from(
                    preview?.querySelectorAll<HTMLButtonElement>('button') ?? []
                );
                const available = buttons.filter(
                    (button) => !button.disabled && !button.closest('[hidden]')
                );
                const trigger = available.find(
                    (button) =>
                        button.getAttribute('aria-controls') === this.panelId()
                );
                (
                    trigger ??
                    available.find(
                        (button) =>
                            button.getAttribute('aria-label') ===
                            'Close preview'
                    )
                )?.focus();
            });
        });
    }
}
